export default function MoodTracker() {
  return (
    <section id="mood-tracker" className="py-24 bg-gradient-to-b from-[#F0F4F8] to-[#E8F1F5]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* Left - Content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-5xl font-serif text-[#2F7FB7] mb-6">The Mood & Anxiety Tracker</h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                The Mood & Anxiety Tracker helps you stay gently connected to your emotional wellbeing over time.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                By checking in regularly, you begin to notice patterns, changes, and progress — not with judgment, but with kindness and curiosity. This awareness supports clearer understanding, emotional insight, and a deeper sense of self-connection as you move through your days.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#E8F1F5] flex-shrink-0">
                  <i className="ri-calendar-check-line text-lg text-[#2F7FB7]"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-[#2F7FB7] mb-1">Daily Check-ins</h4>
                  <p className="text-gray-600">Quick, gentle prompts to record how you're feeling</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#E8F1F5] flex-shrink-0">
                  <i className="ri-bar-chart-line text-lg text-[#2F7FB7]"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-[#2F7FB7] mb-1">Pattern Recognition</h4>
                  <p className="text-gray-600">Visualise trends and understand what influences your mood</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#E8F1F5] flex-shrink-0">
                  <i className="ri-lightbulb-flash-line text-lg text-[#2F7FB7]"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-[#2F7FB7] mb-1">Personalised Insights</h4>
                  <p className="text-gray-600">Receive gentle observations about your emotional journey</p>
                </div>
              </div>
            </div>

            <a href="/download" className="bg-[#2F7FB7] text-white px-8 py-4 rounded-full font-medium hover:bg-[#1E5A8E] transition-all hover:shadow-xl inline-flex items-center gap-3 cursor-pointer whitespace-nowrap group">
              Start Tracking Today
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
            </a>
          </div>

          {/* Right - Visual representation */}
          <div className="relative">
            <div className="bg-white rounded-3xl p-8 shadow-2xl">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-[#2F7FB7] mb-2">Your Weekly Overview</h3>
                <p className="text-sm text-gray-500">Track patterns and celebrate progress</p>
              </div>

              {/* Chart visualization */}
              <div className="space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Monday</span>
                    <span className="text-[#2F7FB7] font-medium">Calm</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#2F7FB7] to-[#5A9FD4] rounded-full" style={{ width: '75%' }}></div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Tuesday</span>
                    <span className="text-[#2F7FB7] font-medium">Peaceful</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#2F7FB7] to-[#5A9FD4] rounded-full" style={{ width: '85%' }}></div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Wednesday</span>
                    <span className="text-[#2F7FB7] font-medium">Balanced</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#2F7FB7] to-[#5A9FD4] rounded-full" style={{ width: '70%' }}></div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Thursday</span>
                    <span className="text-[#2F7FB7] font-medium">Hopeful</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#2F7FB7] to-[#5A9FD4] rounded-full" style={{ width: '80%' }}></div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Friday</span>
                    <span className="text-[#2F7FB7] font-medium">Content</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#2F7FB7] to-[#5A9FD4] rounded-full" style={{ width: '90%' }}></div>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-[#E8F1F5] rounded-2xl">
                <p className="text-sm text-gray-700">
                  <i className="ri-sparkling-line text-[#2F7FB7] mr-2"></i>
                  You've been consistently checking in this week. Your mood shows an upward trend — keep nurturing yourself.
                </p>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#5A9FD4]/10 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-[#E8F1F5]/60 rounded-full blur-2xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
